"use client";

import { AnimatePresence, motion } from "motion/react";
import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { cn } from "../lib/utils";

interface TooltipProperties {
  children: ReactNode;
  content: ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  className?: string;
  delayDuration?: number;
}

export function Tooltip({
  children,
  content,
  side = "top",
  className,
  delayDuration = 200,
}: TooltipProperties) {
  const [isOpen, setIsOpen] = useState(false);
  const timeoutReference = useRef<NodeJS.Timeout | null>(null);
  const tooltipId = useId();

  const showTooltip = useCallback(() => {
    if (timeoutReference.current) {
      clearTimeout(timeoutReference.current);
    }
    timeoutReference.current = setTimeout(() => {
      setIsOpen(true);
    }, delayDuration);
  }, [delayDuration]);

  const hideTooltip = useCallback(() => {
    if (timeoutReference.current) {
      clearTimeout(timeoutReference.current);
    }
    setIsOpen(false);
  }, []);

  // Handle Escape key to close tooltip
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        hideTooltip();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, hideTooltip]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutReference.current) {
        clearTimeout(timeoutReference.current);
      }
    };
  }, []);

  const sideClasses = {
    top: "bottom-full left-1/2 -translate-x-1/2 mb-2",
    bottom: "top-full left-1/2 -translate-x-1/2 mt-2",
    left: "right-full top-1/2 -translate-y-1/2 mr-2",
    right: "left-full top-1/2 -translate-y-1/2 ml-2",
  };

  const arrowClasses = {
    top: "top-full left-1/2 -translate-x-1/2 border-t-gray-900 border-l-transparent border-r-transparent border-b-transparent",
    bottom:
      "bottom-full left-1/2 -translate-x-1/2 border-b-gray-900 border-l-transparent border-r-transparent border-t-transparent",
    left: "left-full top-1/2 -translate-y-1/2 border-l-gray-900 border-t-transparent border-b-transparent border-r-transparent",
    right:
      "right-full top-1/2 -translate-y-1/2 border-r-gray-900 border-t-transparent border-b-transparent border-l-transparent",
  };

  // Clone child element to add accessibility attributes
  const trigger = isValidElement(children)
    ? cloneElement(children as ReactElement<Record<string, unknown>>, {
      "aria-describedby": isOpen ? tooltipId : undefined,
      onMouseEnter: showTooltip,
      onMouseLeave: hideTooltip,
      onFocus: showTooltip,
      onBlur: hideTooltip,
    })
    : children;

  return (
    <div
      className="relative inline-block"
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
    >
      {trigger}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            id={tooltipId}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className={cn(
              "absolute z-50 w-max max-w-xs rounded-lg bg-gray-900 px-3 py-2 text-sm text-white shadow-lg pointer-events-none",
              sideClasses[side],
              className,
            )}
            role="tooltip"
          >
            {content}
            {/* Arrow */}
            <div
              className={cn("absolute h-0 w-0 border-4", arrowClasses[side])}
              aria-hidden="true"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
